import { useState } from "react";
import PropTypes from "prop-types";
import { auth } from "../../firebase";
import {
  updatePassword,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const EditIcon = ({ onClick }) => (
  <button type="button" onClick={onClick} className="p-1">
    <svg
      className="w-5 h-5 text-gray-400 hover:text-white"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M17.414 2.586a2 2 0 010 2.828L8.828 14.012a2 2 0 01-.928.536l-4 1a1 1 0 01-1.212-1.213l1-4a2 2 0 01.536-.928L14.586 2.586a2 2 0 012.828 0zM15 4l-2.586 2.586L13 8l2.586-2.586L15 4z" />
    </svg>
  </button>
);

EditIcon.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const PasswordInput = ({
  label,
  value,
  onChange,
  showPassword,
  toggleVisibility,
  placeholder,
}) => (
  <div className="space-y-2">
    <label className="block text-base font-semibold text-gray-300">
      {label}:
    </label>
    <div className="relative">
      <input
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full pr-10 px-4 py-2 h-12 text-sm sm:text-base leading-tight bg-gray-800/50 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 text-gray-200 placeholder-gray-400"
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggleVisibility}
        className="absolute inset-y-0 right-0 flex items-center pr-3 top-1/2 -translate-y-1/2"
      >
        {showPassword ? (
          <FaEyeSlash size={16} className="text-gray-400 hover:text-white" />
        ) : (
          <FaEye size={16} className="text-gray-400 hover:text-white" />
        )}
      </button>
    </div>
  </div>
);

PasswordInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  showPassword: PropTypes.bool.isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
};

const AdminProfile = ({ onDisplayNameChange }) => {
  const [updatedDisplayName, setUpdatedDisplayName] = useState(
    auth.currentUser?.displayName || ""
  );
  const [updatedPassword, setUpdatedPassword] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [message, setMessage] = useState("");
  const [isEditingDisplayName, setIsEditingDisplayName] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const isDisplayNameChanged =
    isEditingDisplayName &&
    updatedDisplayName.trim() !== "" &&
    updatedDisplayName.trim() !== auth.currentUser?.displayName;
  const isPasswordValid =
    isEditingPassword &&
    updatedPassword.current !== "" &&
    updatedPassword.new !== "" &&
    updatedPassword.confirm !== "" &&
    updatedPassword.new === updatedPassword.confirm;
  const submitEnabled = isDisplayNameChanged || isPasswordValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    const user = auth.currentUser;
    try {
      if (isEditingDisplayName && isDisplayNameChanged) {
        await updateProfile(user, { displayName: updatedDisplayName.trim() });
        onDisplayNameChange(updatedDisplayName.trim());
        setIsEditingDisplayName(false);
      }
      if (isEditingPassword && isPasswordValid) {
        const { current, new: newPass } = updatedPassword;
        if (!current) {
          setMessage(
            "Please enter your current password to update the password"
          );
          return;
        }
        const credential = EmailAuthProvider.credential(user.email, current);
        await reauthenticateWithCredential(user, credential);
        await updatePassword(user, newPass);
        setIsEditingPassword(false);
        setUpdatedPassword({ current: "", new: "", confirm: "" });
      }
      setMessage("Profile updated successfully.");
    } catch (error) {
      if (error.code === "auth/invalid-credential") {
        setMessage("Current password is incorrect");
      } else {
        setMessage(`Error: ${error.message}`);
      }
    }
  };

  return (
    <div className="w-full p-4 sm:p-8 text-gray-200">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center tracking-wide">
        Profile
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
          <label className="text-base font-semibold text-gray-300 mb-1 sm:mb-0">
            Email:
          </label>
          <p className="text-gray-200">{auth.currentUser?.email}</p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
          <label className="text-base font-semibold text-gray-300 mb-1 sm:mb-0">
            Display Name:
          </label>
          <div className="flex items-center">
            <p className="text-gray-200 mr-2">
              {auth.currentUser?.displayName}
            </p>
            <EditIcon
              onClick={() => {
                setIsEditingDisplayName(!isEditingDisplayName);
                setUpdatedDisplayName(auth.currentUser?.displayName || "");
              }}
            />
          </div>
        </div>

        {isEditingDisplayName && (
          <div className="mt-2">
            <input
              type="text"
              value={updatedDisplayName}
              onChange={(e) => setUpdatedDisplayName(e.target.value)}
              placeholder="Enter new display name"
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition duration-200 text-gray-200 placeholder-gray-400"
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
          <label className="text-base font-semibold text-gray-300 mb-1 sm:mb-0">
            Password:
          </label>
          <div className="flex items-center">
            <p className="text-gray-200 mr-2">********</p>
            <EditIcon
              onClick={() => {
                setIsEditingPassword(!isEditingPassword);
                setUpdatedPassword({ current: "", new: "", confirm: "" });
              }}
            />
          </div>
        </div>

        {isEditingPassword && (
          <div className="space-y-4">
            <PasswordInput
              label="Current Password"
              value={updatedPassword.current}
              onChange={(e) =>
                setUpdatedPassword({
                  ...updatedPassword,
                  current: e.target.value,
                })
              }
              showPassword={passwordVisibility.current}
              toggleVisibility={() => togglePasswordVisibility("current")}
              placeholder="Enter current password"
            />

            <PasswordInput
              label="New Password"
              value={updatedPassword.new}
              onChange={(e) =>
                setUpdatedPassword({ ...updatedPassword, new: e.target.value })
              }
              showPassword={passwordVisibility.new}
              toggleVisibility={() => togglePasswordVisibility("new")}
              placeholder="Enter new password"
            />

            <PasswordInput
              label="Confirm New Password"
              value={updatedPassword.confirm}
              onChange={(e) =>
                setUpdatedPassword({
                  ...updatedPassword,
                  confirm: e.target.value,
                })
              }
              showPassword={passwordVisibility.confirm}
              toggleVisibility={() => togglePasswordVisibility("confirm")}
              placeholder="Confirm new password"
            />
          </div>
        )}

        {message && (
          <div className="text-center text-sm text-gray-200 py-2 px-4 rounded-lg bg-gray-800/60">
            {message}
          </div>
        )}

        {(isEditingDisplayName || isEditingPassword) && (
          <button
            type="submit"
            disabled={!submitEnabled}
            className={`w-full py-3 rounded-xl font-bold ${
              submitEnabled
                ? "bg-linear-to-r from-blue-600 to-purple-600 text-white"
                : "bg-gray-500 text-gray-300 cursor-not-allowed"
            }`}
          >
            Update Profile
          </button>
        )}
      </form>
    </div>
  );
};

AdminProfile.propTypes = {
  onDisplayNameChange: PropTypes.func.isRequired,
};

export default AdminProfile;
